/**
 * Module Vuex OIDC — wrapper léger autour de oidcService.
 * Garde la même interface (getters, actions) que vuex-oidc
 * pour minimiser les changements dans les composants.
 */
import oidcService from '@/auth/oidcService'
import store from '@/store/index'
import { MessagesService, ParametersService } from '@/services'
import logger from '@/utils/logger'

// ── Rôles OIDC (configurés via variables d'environnement) ────────────
const ROLE_NIVEAU0 = process.env.VUE_APP_ROLE_NIVEAU0
  ? process.env.VUE_APP_ROLE_NIVEAU0.split(',').map(s => s.trim().toUpperCase())
  : []
const ROLE_ADMIN = process.env.VUE_APP_ROLE_ADMIN
  ? process.env.VUE_APP_ROLE_ADMIN.split(',').map(s => s.trim().toUpperCase())
  : []
const POST_LOGIN_DIALOG_KEY = 'postLoginDialogShown_v2_20260316'

function buildRoles (sub) {
  const roles = []
  const upperSub = sub.toUpperCase()
  if (ROLE_ADMIN.some(x => x === upperSub)) roles.push('admin')
  if (ROLE_NIVEAU0.some(x => x === upperSub)) roles.push('niveau0')
  roles.push('user')
  return roles
}

/**
 * Affiche un dialog d'information une seule fois après la connexion OIDC de l'utilisateur.
 *
 * Même logique que dans Guard.js (pour MSAL), mais ici pour le flux OIDC (utilisé par UQAM).
 *
 * Fonctionnement :
 * 1. Vérifie si le dialog a déjà été affiché dans cette session (via sessionStorage)
 * 2. Si oui, on sort immédiatement — le dialog ne s'affiche pas deux fois
 * 3. Si non, on marque la clé dans sessionStorage pour ne plus le réafficher
 * 4. On commit le message HTML dans le store Vuex (module dialog) pour l'afficher à l'écran
 *
 * La clé est versionnée (_v2_20260316) — changer la version force le réaffichage du message.
 * Appelée dans le callback onUserLoaded() quand l'utilisateur se connecte via OIDC.
 * La clé est effacée dans onUserUnloaded() (logout) pour permettre le réaffichage.
 */
async function showPostLoginDialogOnce () {
  // Si le paramètre getNotificationMessageConnection n'est pas activé ('1'), on n'affiche pas le dialog
  const notificationEnabled = store.getters['parameters/getNotificationMessageConnection']
  if (notificationEnabled !== '1') return

  // Si le dialog a déjà été affiché dans cette session, on ne le réaffiche pas
  if (sessionStorage.getItem(POST_LOGIN_DIALOG_KEY)) return

  // On marque le dialog comme affiché pour cette session
  sessionStorage.setItem(POST_LOGIN_DIALOG_KEY, '1')

  // Récupère le message no 41 depuis l'API
  const messageData = await MessagesService.search(41)
  if (!messageData) return

  // Utilise le message selon la langue de l'application (fr ou en)
  const langue = localStorage.getItem('APP_LANG_KEY') || 'fr'
  const message = messageData[langue] || messageData.fr || ''
  if (!message) return

  // Affiche le dialog via le store Vuex (module dialog)
  store.commit('dialog/showDialog', {
    message, // Contenu HTML du message récupéré de la DB
    print: false, // Pas d'option d'impression
    html: true, // Le message est en HTML (pas du texte brut)
    iconState: true, // Affiche une icône
    iconColor: 'success', // Couleur verte
    iconName: 'mdi-check-circle', // Icône de succès
  })
}

// ── Enregistrement des callbacks sur le service ──────────────────────
oidcService.onUserLoaded(async (user) => {
  // Charger les paramètres avant d'afficher le dialog (même logique que loadParameters dans Guard.js)
  if (!store.state.parameters.parameters.length) {
    try {
      const parameters = await ParametersService.listAll()
      store.commit('parameters/setParameters', parameters)
    } catch (error) {
      logger.info('Erreur chargement paramètres OIDC:', error)
    }
  }

  await showPostLoginDialogOnce()

  store.commit('userInfo/updateUser', {
    name: user.profile.family_name,
    firstName: user.profile.given_name,
    mail: user.profile.email,
    displayName: `${user.profile.family_name}, ${user.profile.given_name}`,
    username: user.profile.email,
    roles: buildRoles(user.profile.sub),
    codeMs: user.profile.sub,
  })

  store.commit('oidcStore/setUser', user)
})

oidcService.onUserUnloaded(() => {
  sessionStorage.removeItem(POST_LOGIN_DIALOG_KEY)
  store.commit('userInfo/resetUser')
  // On vide les paramètres pour forcer le rechargement à la prochaine connexion
  store.commit('parameters/setParameters', [])
  store.commit('oidcStore/unsetOidcAuth')
})

let authenticateSilentListener = null

oidcService.onAccessTokenExpiring(() => {
  authenticateSilentListener = () => {
    store.dispatch('oidcStore/authenticateOidcSilent')
    document.removeEventListener('mousemove', authenticateSilentListener)
  }
  document.addEventListener('mousemove', authenticateSilentListener)
})

oidcService.onAccessTokenExpired(() => {
  if (authenticateSilentListener) {
    document.removeEventListener('mousemove', authenticateSilentListener)
  }
  store.dispatch('oidcStore/signOutOidc')
})

// Initialiser le service au chargement
oidcService.init()

// ── Module Vuex ──────────────────────────────────────────────────────
export default {
  namespaced: true,

  state: () => ({
    user: oidcService.user,
    access_token: oidcService.accessToken,
    id_token: oidcService.idToken,
    is_checked: oidcService.authenticationIsChecked,
  }),

  getters: {
    oidcIsAuthenticated: () => oidcService.isAuthenticated,
    oidcAuthenticationIsChecked: () => oidcService.authenticationIsChecked,
    oidcUser: () => oidcService.profile,
    oidcIdToken: () => oidcService.idToken,
    oidcIdTokenExp: () => oidcService.idTokenExp,
    oidcAccessToken: () => oidcService.accessToken,
  },

  mutations: {
    setUser (state, user) {
      state.user = user
      state.access_token = user ? user.access_token : null
      state.id_token = user ? user.id_token : null
      state.is_checked = true
    },
    unsetOidcAuth (state) {
      state.user = null
      state.access_token = null
      state.id_token = null
    },
  },

  actions: {
    async authenticateOidc () {
      return oidcService.signIn()
    },
    async authenticateOidcSilent () {
      try {
        return await oidcService.signInSilent()
      } catch (error) {
        logger.info('OIDC silent auth error:', error)
      }
    },
    async oidcSignInCallback () {
      return oidcService.signInCallback()
    },
    async signOutOidc () {
      return oidcService.signOut()
    },
    async getOidcUser () {
      return oidcService.user
    },
  },
}
